import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Textarea,
  Alert,
  AlertIcon,
  Spinner,
  Badge,
  Icon,
  Divider
} from '@chakra-ui/react';
import { FaPlus, FaMinus, FaWallet, FaUser } from 'react-icons/fa';
import { useWalletContext } from '../../context/WalletContext';
import { useAuth } from '../../hooks/useAuth';

const MerchantWalletControls = ({ artistId, artistName }) => {
  const { addFundsToArtist, withdrawFundsFromArtist, loading } = useWalletContext();
  const { user } = useAuth();
  const toast = useToast();

  // Modal states
  const { isOpen: isAddFundsOpen, onOpen: onAddFundsOpen, onClose: onAddFundsClose } = useDisclosure();
  const { isOpen: isWithdrawOpen, onOpen: onWithdrawOpen, onClose: onWithdrawClose } = useDisclosure();

  // Form states
  const [addFundsForm, setAddFundsForm] = useState({
    amount: '',
    description: ''
  });
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Validation
  const validateAmount = (amount) => {
    if (!amount || amount === '') return 'Amount is required';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'Amount must be a valid number';
    if (numAmount < 1) return 'Minimum amount is $1.00';
    if (numAmount > 50000) return 'Maximum amount is $50,000.00';
    return null;
  };

  // Add funds handler
  const handleAddFunds = async () => {
    setFormErrors({});

    const amountError = validateAmount(addFundsForm.amount);
    if (amountError) {
      setFormErrors({ amount: amountError });
      return;
    }

    try {
      setSubmitting(true);
      await addFundsToArtist(artistId, {
        amount: parseFloat(addFundsForm.amount),
        description: addFundsForm.description || undefined
      });

      // Reset form and close modal
      setAddFundsForm({ amount: '', description: '' });
      onAddFundsClose();

      toast({
        title: 'Funds Added Successfully',
        description: `$${parseFloat(addFundsForm.amount).toFixed(2)} has been added to ${artistName}'s wallet`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error adding funds:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Withdraw funds handler
  const handleWithdrawFunds = async () => {
    setFormErrors({});

    const amountError = validateAmount(withdrawForm.amount);
    if (amountError) {
      setFormErrors({ amount: amountError });
      return;
    }

    try {
      setSubmitting(true);
      await withdrawFundsFromArtist(artistId, {
        amount: parseFloat(withdrawForm.amount),
        description: withdrawForm.description || undefined
      });

      // Reset form and close modal
      setWithdrawForm({ amount: '', description: '' });
      onWithdrawClose();

      toast({
        title: 'Funds Withdrawn Successfully',
        description: `$${parseFloat(withdrawForm.amount).toFixed(2)} has been withdrawn from ${artistName}'s wallet`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error withdrawing funds:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleAddFundsClose = () => {
    setAddFundsForm({ amount: '', description: '' });
    setFormErrors({});
    onAddFundsClose();
  };

  const handleWithdrawClose = () => {
    setWithdrawForm({ amount: '', description: '' });
    setFormErrors({});
    onWithdrawClose();
  };

  if (user?.account_type !== 'merchant') {
    return null;
  }

  return (
    <>
      <Box bg="dark.500" borderRadius="xl" p={6} border="1px solid" borderColor="dark.300">
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Icon as={FaWallet} color="primary.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="bold" color="white">
                Wallet Controls
              </Text>
            </HStack>
            <Badge colorScheme="blue" variant="subtle">
              Merchant
            </Badge>
          </HStack>

          <Alert status="info" bg="blue.900" borderColor="blue.600" borderRadius="md">
            <AlertIcon color="blue.300" />
            <Text fontSize="sm" color="blue.200">
              You have full control over {artistName}'s wallet as their managing merchant.
            </Text>
          </Alert>

          <Divider borderColor="dark.300" />

          <HStack spacing={4}>
            <Button
              leftIcon={<FaPlus />}
              colorScheme="green"
              variant="solid"
              onClick={onAddFundsOpen}
              isLoading={loading}
              flex={1}
              size="md"
            >
              Add Funds
            </Button>
            <Button
              leftIcon={<FaMinus />}
              colorScheme="orange"
              variant="solid"
              onClick={onWithdrawOpen}
              isLoading={loading}
              flex={1}
              size="md"
            >
              Withdraw
            </Button>
          </HStack>

          <Text fontSize="xs" color="gray.400" textAlign="center">
            All transactions are recorded and audited for security
          </Text>
        </VStack>
      </Box>

      {/* Add Funds Modal */}
      <Modal isOpen={isAddFundsOpen} onClose={handleAddFundsClose} size="md">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="dark.600" borderColor="dark.300">
          <ModalHeader color="white">
            <HStack spacing={3}>
              <Icon as={FaPlus} color="green.400" />
              <Text>Add Funds to {artistName}'s Wallet</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={formErrors.amount}>
                <FormLabel color="gray.300">Amount ($)</FormLabel>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={addFundsForm.amount}
                  onChange={(e) => setAddFundsForm(prev => ({ ...prev, amount: e.target.value }))}
                  bg="dark.500"
                  border="1px solid"
                  borderColor="dark.300"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                  _focus={{ borderColor: 'primary.500', boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)' }}
                  min="1"
                  max="50000"
                  step="0.01"
                />
                <FormErrorMessage>{formErrors.amount}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">Description (Optional)</FormLabel>
                <Textarea
                  placeholder="e.g., Marketing budget allocation"
                  value={addFundsForm.description}
                  onChange={(e) => setAddFundsForm(prev => ({ ...prev, description: e.target.value }))}
                  bg="dark.500"
                  border="1px solid"
                  borderColor="dark.300"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                  _focus={{ borderColor: 'primary.500', boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)' }}
                  rows={3}
                  maxLength={500}
                />
              </FormControl>

              <Alert status="warning" bg="yellow.900" borderColor="yellow.600" borderRadius="md">
                <AlertIcon color="yellow.300" />
                <Text fontSize="sm" color="yellow.200">
                  This will add funds directly to the artist's available balance.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleAddFundsClose}
              color="gray.400"
              _hover={{ bg: 'dark.500' }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleAddFunds}
              isLoading={submitting}
              loadingText="Adding Funds"
              disabled={!addFundsForm.amount || submitting}
            >
              Add Funds
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Withdraw Funds Modal */}
      <Modal isOpen={isWithdrawOpen} onClose={handleWithdrawClose} size="md">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="dark.600" borderColor="dark.300">
          <ModalHeader color="white">
            <HStack spacing={3}>
              <Icon as={FaMinus} color="orange.400" />
              <Text>Withdraw from {artistName}'s Wallet</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={formErrors.amount}>
                <FormLabel color="gray.300">Amount ($)</FormLabel>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm(prev => ({ ...prev, amount: e.target.value }))}
                  bg="dark.500"
                  border="1px solid"
                  borderColor="dark.300"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                  _focus={{ borderColor: 'primary.500', boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)' }}
                  min="1"
                  max="50000"
                  step="0.01"
                />
                <FormErrorMessage>{formErrors.amount}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">Reason (Optional)</FormLabel>
                <Textarea
                  placeholder="e.g., Commission withdrawal, expense reimbursement"
                  value={withdrawForm.description}
                  onChange={(e) => setWithdrawForm(prev => ({ ...prev, description: e.target.value }))}
                  bg="dark.500"
                  border="1px solid"
                  borderColor="dark.300"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                  _focus={{ borderColor: 'primary.500', boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)' }}
                  rows={3}
                  maxLength={500}
                />
              </FormControl>

              <Alert status="warning" bg="orange.900" borderColor="orange.600" borderRadius="md">
                <AlertIcon color="orange.300" />
                <Text fontSize="sm" color="orange.200">
                  This will remove funds from the artist's available balance. Ensure sufficient funds are available.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleWithdrawClose}
              color="gray.400"
              _hover={{ bg: 'dark.500' }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleWithdrawFunds}
              isLoading={submitting}
              loadingText="Withdrawing"
              disabled={!withdrawForm.amount || submitting}
            >
              Withdraw Funds
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MerchantWalletControls;